import { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UploadSimple } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import { FormInput } from '../ui/FormInput';
import { useUploadDocument } from '../../services/documents.service';

interface UploadDocumentModalProps {
  companyId: string;
  allowDocx?: boolean;
}

export interface UploadDocumentModalRef {
  open: (parentId: string | null) => void;
}

interface DocumentFormData {
  name: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const PDF_MIME_TYPES = ['application/pdf'];
const EXTENDED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const UploadDocumentModal = forwardRef<UploadDocumentModalRef, UploadDocumentModalProps>(
  ({ companyId, allowDocx = false }, ref) => {
    const allowedMimeTypes = allowDocx
      ? [...PDF_MIME_TYPES, ...EXTENDED_MIME_TYPES]
      : PDF_MIME_TYPES;
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [parentId, setParentId] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);

    const {
      register,
      handleSubmit: handleFormSubmit,
      formState: { errors },
      reset,
      setValue,
      watch,
    } = useForm<DocumentFormData>({
      defaultValues: { name: '' },
    });

    const documentName = watch('name');
    const uploadDocumentMutation = useUploadDocument(companyId);

    useImperativeHandle(ref, () => ({
      open: (parentId: string | null) => {
        setParentId(parentId);
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        reset();
        setSelectedFile(null);
        setFileError(null);
        setIsDragOver(false);
      }
    }, [isOpen, reset]);

    const validateFile = useCallback(
      (file: File): boolean => {
        setFileError(null);

        if (!allowedMimeTypes.includes(file.type)) {
          setFileError(
            allowDocx
              ? t('documents.upload_modal.errors.invalid_format_docx')
              : t('documents.upload_modal.errors.invalid_format'),
          );
          return false;
        }

        if (file.size > MAX_FILE_SIZE) {
          setFileError(t('documents.upload_modal.errors.file_too_large'));
          return false;
        }

        return true;
      },
      [t, allowDocx, allowedMimeTypes],
    );

    const handleFileSelect = useCallback(
      (file: File) => {
        if (validateFile(file)) {
          setSelectedFile(file);
          if (!documentName) {
            const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
            setValue('name', nameWithoutExtension);
          }
        }
      },
      [validateFile, documentName, setValue],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const file = e.dataTransfer.files[0];
        if (file) {
          handleFileSelect(file);
        }
      },
      [handleFileSelect],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileSelect(file);
        }
      },
      [handleFileSelect],
    );

    const handleSubmit = (data: DocumentFormData) => {
      if (!selectedFile) return;

      uploadDocumentMutation.mutate(
        { name: data.name, parentId, file: selectedFile },
        { onSuccess: handleClose },
      );
    };

    const handleClose = () => {
      reset();
      setSelectedFile(null);
      setFileError(null);
      setIsOpen(false);
    };

    const isLoading = uploadDocumentMutation.isPending;
    const canSubmit = !!selectedFile && !!documentName && !fileError;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 bg-gray-100 rounded-md text-gray-900 font-medium hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]"
          disabled={isLoading}
        >
          {t('documents.upload_modal.cancel')}
        </button>
        <button
          type="submit"
          form="upload-document-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md font-medium hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading || !canSubmit}
        >
          {isLoading ? t('documents.upload_modal.uploading') : t('documents.upload_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.upload_modal.title')}
        footer={footer}
        maxWidth="lg"
      >
        <form
          id="upload-document-form"
          onSubmit={handleFormSubmit(handleSubmit)}
          className="space-y-6"
        >
          <FormInput
            label={t('documents.upload_modal.name.label')}
            type="text"
            placeholder={t('documents.upload_modal.name.placeholder')}
            error={errors.name?.message}
            required
            {...register('name', {
              required: t('documents.upload_modal.name.error.required'),
            })}
          />

          <div className="flex flex-col gap-3">
            <label className="text-base font-medium text-gray-900">
              {t('documents.upload_modal.dropzone.label')}
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                flex flex-col items-center justify-center gap-3 p-6
                border border-dashed rounded-md transition-colors
                ${isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white'}
                ${selectedFile ? 'border-green-500 bg-green-50' : ''}
              `}
            >
              <UploadSimple size={48} className="text-gray-400" />

              {selectedFile ? (
                <div className="text-center">
                  <p className="text-sm text-gray-900 font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} Mo
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center">
                  {t('documents.upload_modal.dropzone.hint')}
                </p>
              )}

              {fileError && <p className="text-sm text-red-600">{fileError}</p>}

              <label className="cursor-pointer">
                <input
                  type="file"
                  accept={
                    allowDocx
                      ? '.pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation'
                      : '.pdf,application/pdf'
                  }
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="inline-flex px-4 py-2 bg-gray-100 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]">
                  {t('documents.upload_modal.dropzone.button')}
                </span>
              </label>
            </div>
          </div>
        </form>
      </BaseModal>
    );
  },
);

UploadDocumentModal.displayName = 'UploadDocumentModal';
